/**
 * Hook for Activities layout persistence: load/save/list/delete/reset and debounced auto-save.
 * Scope = scrollPersistKey (e.g. "activities.123"). When undefined, no storage access.
 */

import { useEffect, useRef } from 'react';
import type { LayoutState } from '@/components/ActivityTable/types';
import { DEFAULT_LAYOUT } from '@/components/ActivityTable/activityLayoutDefaults';

const PREFIX = 'activities.layout.';
const AUTO_SUFFIX = '.auto';
const SAVED_PREFIX_SUFFIX = '.saved.';
const ACTIVE_SUFFIX = '.active';
const DEBOUNCE_MS = 500;

function keyAuto(scope: string): string {
  return `${PREFIX}${scope}${AUTO_SUFFIX}`;
}

function keySaved(scope: string, layoutName: string): string {
  const safe = layoutName.replace(/\./g, '_').trim() || 'unnamed';
  return `${PREFIX}${scope}${SAVED_PREFIX_SUFFIX}${safe}`;
}

function keyActive(scope: string): string {
  return `${PREFIX}${scope}${ACTIVE_SUFFIX}`;
}

function safeMerge(parsed: unknown): LayoutState {
  const def = DEFAULT_LAYOUT;
  if (parsed == null || typeof parsed !== 'object') return { ...def };

  const p = parsed as Record<string, unknown>;

  const columnState = Array.isArray(p.columnState)
    ? (p.columnState as unknown[]).map((item) => {
        const d = def.columnState[0] ?? { key: 'id', visible: true, order: 0, decimalsEnabled: false, decimalPlaces: 2 };
        if (item == null || typeof item !== 'object') return d;
        const o = item as Record<string, unknown>;
        return {
          key: typeof o.key === 'string' || typeof o.key === 'number' ? o.key : d.key,
          visible: typeof o.visible === 'boolean' ? o.visible : d.visible,
          order: typeof o.order === 'number' ? o.order : d.order,
          decimalsEnabled: typeof o.decimalsEnabled === 'boolean' ? o.decimalsEnabled : d.decimalsEnabled,
          decimalPlaces: typeof o.decimalPlaces === 'number' ? o.decimalPlaces : d.decimalPlaces,
        };
      })
    : def.columnState;

  let columnSizing: Record<string, number> = def.columnSizing;
  if (p.columnSizing != null && typeof p.columnSizing === 'object' && !Array.isArray(p.columnSizing)) {
    columnSizing = {};
    for (const [k, v] of Object.entries(p.columnSizing)) {
      if (typeof v === 'number' && Number.isFinite(v)) columnSizing[k] = v;
    }
  }

  const groupBy =
    p.groupBy === 'wbs' || p.groupBy === 'none' ? p.groupBy : def.groupBy;

  let tableSettings = def.tableSettings;
  if (p.tableSettings != null && typeof p.tableSettings === 'object') {
    const ts = p.tableSettings as Record<string, unknown>;
    tableSettings = {
      highlightCritical: typeof ts.highlightCritical === 'boolean' ? ts.highlightCritical : def.tableSettings.highlightCritical,
      showTime: typeof ts.showTime === 'boolean' ? ts.showTime : def.tableSettings.showTime,
    };
  }

  const floatDisplayUnit =
    p.floatDisplayUnit === 'days' || p.floatDisplayUnit === 'hours' ? p.floatDisplayUnit : def.floatDisplayUnit;

  return {
    columnState: columnState.length > 0 ? columnState : def.columnState,
    columnSizing,
    groupBy,
    tableSettings,
    floatDisplayUnit,
  };
}

export function loadLayout(scope: string | undefined, layoutName?: string): LayoutState | null {
  if (!scope) return null;
  try {
    const key = layoutName ? keySaved(scope, layoutName) : keyAuto(scope);
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return safeMerge(parsed);
  } catch {
    return null;
  }
}

export function saveLayout(scope: string | undefined, state: LayoutState, layoutName?: string): void {
  if (!scope) return;
  try {
    if (layoutName) {
      const safe = layoutName.replace(/\./g, '_').trim() || 'unnamed';
      localStorage.setItem(keySaved(scope, safe), JSON.stringify(state));
      localStorage.setItem(keyActive(scope), safe);
    } else {
      localStorage.setItem(keyAuto(scope), JSON.stringify(state));
      localStorage.setItem(keyActive(scope), 'auto');
    }
  } catch {
    /* ignore */
  }
}

export function listSavedLayouts(scope: string | undefined): string[] {
  if (!scope) return [];
  const prefix = `${PREFIX}${scope}${SAVED_PREFIX_SUFFIX}`;
  const names: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(prefix)) {
        const name = k.slice(prefix.length);
        if (name) names.push(name);
      }
    }
  } catch {
    /* ignore */
  }
  return names.sort();
}

export function deleteLayout(scope: string | undefined, layoutName: string): void {
  if (!scope) return;
  try {
    const key = keySaved(scope, layoutName);
    localStorage.removeItem(key);
    const active = localStorage.getItem(keyActive(scope));
    if (active === layoutName) localStorage.setItem(keyActive(scope), 'auto');
  } catch {
    /* ignore */
  }
}

export function getActiveLayoutName(scope: string | undefined): string {
  if (!scope) return 'auto';
  try {
    const v = localStorage.getItem(keyActive(scope));
    return v === 'auto' || !v ? 'auto' : v;
  } catch {
    return 'auto';
  }
}

export function setActiveLayoutName(scope: string | undefined, name: string): void {
  if (!scope) return;
  try {
    localStorage.setItem(keyActive(scope), name === 'auto' ? 'auto' : name);
  } catch {
    /* ignore */
  }
}

export function resetToDefault(scope: string | undefined): LayoutState {
  if (scope) {
    try {
      localStorage.removeItem(keyAuto(scope));
      localStorage.setItem(keyActive(scope), 'auto');
    } catch {
      /* ignore */
    }
  }
  return {
    ...DEFAULT_LAYOUT,
    columnState: DEFAULT_LAYOUT.columnState.map((c) => ({ ...c })),
    columnSizing: { ...DEFAULT_LAYOUT.columnSizing },
    tableSettings: { ...DEFAULT_LAYOUT.tableSettings },
  };
}

export interface UseActivityLayoutOptions {
  scope: string | undefined;
  state: LayoutState;
  /** When true, auto-save is skipped (e.g. a named layout is currently active). */
  skipAutoSave?: boolean;
}

export function useActivityLayout({ scope, state, skipAutoSave = false }: UseActivityLayoutOptions): void {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!scope || skipAutoSave) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      try {
        const active = localStorage.getItem(keyActive(scope));
        if (active !== 'auto' && active != null && active !== '') return;
        localStorage.setItem(keyAuto(scope), JSON.stringify(state));
        localStorage.setItem(keyActive(scope), 'auto');
      } catch {
        /* ignore */
      }
    }, DEBOUNCE_MS);
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [scope, skipAutoSave, state]);
}
